import type { OwnerSquad } from '@/analytics/generated/segment'
import { getGuideTourStepRoute } from '@/guide-tour/routing/utils'
import type { GuideTourTrackingSnapshot } from '@/guide-tour/tracking'
import type { GuideTourInteractionAction } from '@/guide-tour/notifications/types'
import type { GuideTourDefinition, GuideTourStep } from '@/guide-tour/types'
import { pathNameToSegmentScreenName } from '@/resources/utils/analytics'

type GuideTourMapSource = Partial<{
  tour: GuideTourDefinition
  step: GuideTourStep
  currentStep: number
  totalStep: number
  tourCurrent: number
  tourTotal: number
  pathname: string
  route: string | null
  action: GuideTourInteractionAction
}>

const mapGuideTourBaseProps = (source: GuideTourMapSource) => {
  const { tour, step } = source
  const stepRoute =
    tour && step
      ? (getGuideTourStepRoute(tour, step) ?? undefined)
      : (source.route ?? source.pathname ?? undefined)

  return {
    owner_squad: tour?.ownerSquad as OwnerSquad,
    step_badge: step?.badge,
    step_current: source.currentStep,
    step_route: stepRoute,
    step_title: step?.title,
    step_total: source.totalStep,
    target_id: step?.targetId,
    tour_campaign_id: tour?.campaignId,
    tour_current: source.tourCurrent,
    tour_id: tour?.id,
    tour_route: tour?.route,
    tour_total: source.tourTotal,
    tour_type: tour?.type,
    tour_version:
      tour?.version !== undefined ? String(tour.version) : undefined,
  }
}

export const mapGuideTourViewedProps = mapGuideTourBaseProps

export const mapGuideTourClickedProps = (
  source: GuideTourMapSource,
  componentName: unknown
) => ({
  ...mapGuideTourBaseProps(source),
  component_category: 'button',
  component_name: componentName,
  tour_action: source.action,
})

/** Interceptor rewrite: tour fields + `component_name` from the feature event. */
export const mapGuideTourTargetInteractionClickedProps = (
  snapshot: GuideTourTrackingSnapshot,
  foreignProperties?: Record<string, unknown>
) => ({
  ...mapGuideTourClickedProps(
    {
      ...snapshot,
      action: foreignProperties?.action as
        | GuideTourInteractionAction
        | undefined,
    },
    foreignProperties?.component_name
  ),
  component_category: foreignProperties?.component_category,
  screen_name: pathNameToSegmentScreenName(snapshot.pathname ?? null),
})
